import { PatientReimbursement, ReimbursementUnavailabilityReason } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { assertUnreachable, TranslateFunction, useTranslate } from '@dreryk/react-common';
import { AddCircleIcon, IconSize, IconWrapper } from '@dreryk/react-components';

import { shouldDistinctReimbursementByVisitDiagnoses } from '~libs.shared.prescription';

import { translationKeys } from '../../translations';
import { Reimbursement } from '../Reimbursement/Reimbursement';
import { OnReimbursementClick, ReimbursementVariant } from '../Reimbursement/Reimbursement.types';

import { StyledMedicineReimbursementSelector } from './MedicineReimbursementSelector.styled';

type MedicineReimbursementSelectorProps = {
   readonly reimbursements: PatientReimbursement[];
   readonly onReimbursementClick?: OnReimbursementClick;
   readonly disabled: boolean;
   readonly visitDiagnoses: VisitDiagnosis[];
};

const createUnavailableReimbursementTooltipContent = (
   unavailabilityReason: ReimbursementUnavailabilityReason,
   translate: TranslateFunction
): string => {
   switch (unavailabilityReason) {
      case 'S_AND_DZ_UNAVAILABLE_FOR_PRIVATE_HEALTHCARE_PROVIDERS':
         return translate(
            translationKeys.prescription.prescriptionUnavailability
               .S_AND_DZ_UNAVAILABLE_FOR_PRIVATE_HEALTHCARE_PROVIDERS
         );
      default:
         return assertUnreachable(unavailabilityReason);
   }
};

export const MedicineReimbursementSelector = (props: MedicineReimbursementSelectorProps): JSX.Element | null => {
   const translate = useTranslate();

   if (!props.reimbursements.length) {
      return null;
   }

   const tooltipContent = props.onReimbursementClick
      ? translate(translationKeys.medicine.addMedicineToPrescription)
      : undefined;

   return (
      <StyledMedicineReimbursementSelector>
         {props.reimbursements.map((reimbursement) => {
            if (reimbursement.unavailabilityReason) {
               return (
                  <Reimbursement
                     key={reimbursement.code}
                     code={reimbursement.code}
                     priceDescription={reimbursement.priceDescription}
                     variant={ReimbursementVariant.PRIMARY}
                     tooltipContent={createUnavailableReimbursementTooltipContent(
                        reimbursement.unavailabilityReason,
                        translate
                     )}
                     disabled
                  />
               );
            }
            if (props.disabled) {
               return (
                  <Reimbursement
                     key={reimbursement.code}
                     code={reimbursement.code}
                     priceDescription={reimbursement.priceDescription}
                     variant={ReimbursementVariant.PRIMARY}
                     disabled
                  />
               );
            }
            return (
               <Reimbursement
                  key={reimbursement.code}
                  code={reimbursement.code}
                  priceDescription={reimbursement.priceDescription}
                  variant={ReimbursementVariant.PRIMARY}
                  onClick={props.onReimbursementClick}
                  distinct={shouldDistinctReimbursementByVisitDiagnoses(
                     reimbursement.eligibleIcd10Codes,
                     props.visitDiagnoses
                  )}
                  icon={
                     <IconWrapper size={IconSize.AUTO} testId={'medicineReimbursementAdd'}>
                        <AddCircleIcon />
                     </IconWrapper>
                  }
                  tooltipContent={tooltipContent}
               />
            );
         })}
      </StyledMedicineReimbursementSelector>
   );
};
